import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Button, FormWrapper, ImageUploadModal, ModalWrapper, SubmitButton, ViewMediaModal } from "../../components";
import { UploadFileTypes } from "../../constants";
import { useCompany } from "../../hooks";

function CreateCompanyModal({ isVisible, handleClose }) {
  const [formData, setFormData] = useState({
    company_name: "",
    logo: "",
    owner: {
      name: "",
      mobile: "",
      gender: "",
      age: "",
      occupation: "",
      address: "",
      aadhar_number: "",
      aadhar_document_id: "",
      pan_number: "",
      pan_document_id: "",
      email: "",
    },
  });
  const [showOwnerDetailsForm, setShowOwnerDetailsForm] = useState(false);
  const [showViewMediaModal, setShowViewMediaModal] = useState(false);
  const [viewMediaOptions, setViewMediaOptions] = useState({});

  const handleCompanyDetailsInputChange = (e, useTextCase) => {
    const { name, value } = e.target;
    let newValue = value;
    if (useTextCase === "capitalize")
      newValue = value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));

    setFormData({ ...formData, [name]: newValue });
  };

  const handleOwnerDetailsInputChange = (e, useTextCase) => {
    const { name, value } = e.target;
    let newValue = value;
    if (useTextCase === "capitalize")
      newValue = value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));
    if (useTextCase === "uppercase") newValue = value.toUpperCase();
    if (useTextCase === "lowercase") newValue = value.toLowerCase();
    setFormData({
      ...formData,
      owner: { ...formData.owner, [name]: newValue },
    });
  };

  const validateNewCompanyData = () => {
    if (!formData.company_name || formData.company_name === "") {
      toast.error("Please enter a valid company name");
      return false;
    }
    if (!formData.logo) {
      toast.error("Please upload a valid company logo");
      return false;
    }

    return true;
  };

  const handleSaveCompany = (e) => {
    e.preventDefault();
    const isValidData = validateNewCompanyData();
    if (!isValidData) return;
    setShowOwnerDetailsForm(true);
  };

  const validateOwnerData = () => {
    if (!formData.owner.aadhar_document_id || formData.owner.aadhar_document_id === "") {
      toast.error("Please upload a valid aadhar card photo");
      return false;
    }

    if (!formData.owner.pan_document_id || formData.owner.pan_document_id === "") {
      toast.error("Please upload a valid pan card photo");
      return false;
    }

    const aadharRegex = /^[2-9]{1}[0-9]{3}[-\s]?[0-9]{4}[-\s]?[0-9]{4}$/;
    if (!aadharRegex.test(formData.owner.aadhar_number)) {
      toast.error("Please enter a valid aadhar number");
      return false;
    }

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(formData.owner.pan_number)) {
      toast.error("Please enter a valid pan number");
      return false;
    }

    if (Object.values(formData.owner).some((value) => !value || value === "")) {
      toast.error("Please enter valid owner details");
      return false;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.owner.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const { addNewCompany } = useCompany();
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    const isValidData = validateOwnerData();
    if (!isValidData) return;
    const data = formData;

    const { company, error } = await addNewCompany(data);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success("Company created successfully");
    handleClose(company);
  };

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [uploadOptions, setUploadOptions] = useState([]);
  const [uploads, setUploads] = useState({
    logo_url: "",
    aadhar_document_url: "",
    pan_document_url: "",
  });

  const handleImageUpload = (file_id, url, key) => {
    if (key === "logo") setFormData((prev) => ({ ...prev, logo: file_id }));
    else setFormData((prev) => ({ ...prev, owner: { ...prev.owner, [`${key}_id`]: file_id } }));
    setUploads((prev) => ({ ...prev, [`${key}_url`]: url }));
    setShowFileUploadModal(false);
  };

  const handleLogoUpload = () => {
    setUploadOptions({
      title: "New Company Logo",
      handleUpload: (id, url) => handleImageUpload(id, url, "logo"),
    });
    setShowFileUploadModal(true);
  };

  const handleAadharUpload = () => {
    setUploadOptions({
      title: "Project Owner Aadhar Card",
      handleUpload: (id, url) => handleImageUpload(id, url, "aadhar_document"),
    });
    setShowFileUploadModal(true);
  };

  const handlePanUpload = () => {
    setUploadOptions({
      title: "Project Owner PAN Card",
      handleUpload: (id, url) => handleImageUpload(id, url, "pan_document"),
    });
    setShowFileUploadModal(true);
  };

  const handleViewLogo = () => {
    setViewMediaOptions({ title: "Company Logo", url: [uploads.logo_url], mediaType: "image" });
    setShowViewMediaModal(true);
  };

  const handleViewAadhar = () => {
    setViewMediaOptions({
      title: "Project Owner Aadhar Card",
      url: [uploads.aadhar_document_url],
      mediaType: "image",
    });
    setShowViewMediaModal(true);
  };

  const handleViewPan = () => {
    setViewMediaOptions({
      title: "Project Owner PAN Card",
      url: [uploads.pan_document_url],
      mediaType: "image",
    });
    setShowViewMediaModal(true);
  };

  return (
    <>
      <ModalWrapper isVisible={isVisible} onClose={handleClose} name="create-company-modal">
        {!showOwnerDetailsForm ? (
          <FormWrapper id="company-details-form" title="Add New Company" onSubmit={handleSaveCompany}>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={(e) => handleCompanyDetailsInputChange(e, "capitalize")}
              placeholder="Enter Company Name"
              className="input"
              required
              pattern="([a-zA-Z0-9\s+]+)"
              maxLength={50}
              title="Only alphabets and numbers are allowed"
            />
            <div className="row">
              <span className="label">Upload Company Logo</span>
              <div className="end row">
                {uploads.logo_url ? (
                  <Button className="ghost_gray" onClick={handleViewLogo}>
                    Preview
                  </Button>
                ) : (
                  <span className="preview small gray">No logo uploaded</span>
                )}
                <Button className="upload" onClick={handleLogoUpload}>
                  Upload
                </Button>
              </div>
            </div>
            <SubmitButton form="company-details-form" end>
              Save
            </SubmitButton>
          </FormWrapper>
        ) : (
          <FormWrapper id="owner-details-form" onSubmit={handleCreateCompany} title="Project Owner Details">
            <input
              type="text"
              name="name"
              value={formData.owner.name}
              onChange={(e) => handleOwnerDetailsInputChange(e, "capitalize")}
              placeholder="Enter Full Name (as per aadhar)"
              className="input"
              required
              pattern="([a-zA-Z\s+]+)"
              title="Only alphabets are allowed"
              maxLength={50}
            />
            <input
              type="tel"
              name="mobile"
              value={formData.owner.mobile}
              onChange={handleOwnerDetailsInputChange}
              placeholder="Enter Mobile Number"
              className="input"
              required
              pattern="[0-9]{10}"
              minLength={10}
              maxLength={10}
              title="Only numbers are allowed"
            />
            <input
              type="email"
              name="email"
              value={formData.owner.email}
              onChange={handleOwnerDetailsInputChange}
              placeholder="Enter Email Address"
              className="input"
              required
            />
            <div className="row">
              <input
                type="number"
                name="age"
                value={formData.owner.age}
                onChange={handleOwnerDetailsInputChange}
                placeholder="Enter Age"
                className="input full"
                required
                min={18}
                max={100}
              />
              <input
                type="text"
                name="gender"
                value={formData.owner.gender}
                onChange={(e) => handleOwnerDetailsInputChange(e, "lowercase")}
                placeholder="Enter Gender"
                className="input full"
                required
                pattern="male|female|other"
                title="Only 'male', 'female' and 'other' are allowed"
              />
            </div>
            <input
              type="text"
              name="occupation"
              value={formData.owner.occupation}
              onChange={(e) => handleOwnerDetailsInputChange(e, "capitalize")}
              placeholder="Enter Occupation"
              className="input"
              required
              pattern="([a-zA-Z\s+]+)"
              title="Only alphabets are allowed"
              maxLength={50}
            />
            <input
              type="text"
              name="address"
              value={formData.owner.address}
              onChange={handleOwnerDetailsInputChange}
              placeholder="Enter Residential Address"
              className="input"
              required
            />
            <div className="row">
              <input
                type="text"
                name="aadhar_number"
                value={formData.owner.aadhar_number}
                onChange={handleOwnerDetailsInputChange}
                placeholder="Enter Aadhar Number"
                className="input full"
                required
                minLength={12}
                maxLength={12}
                pattern="^([2-9]{1}[0-9]{11})$"
                title="Aadhar numbers cannot start with 0 or 1, and must be 12 digits long"
              />
              <div className="end row full">
                {uploads.aadhar_document_url ? (
                  <Button className="ghost_gray" onClick={handleViewAadhar}>
                    Preview
                  </Button>
                ) : (
                  <span className="preview small gray">No image uploaded</span>
                )}
                <Button className="upload" onClick={handleAadharUpload}>
                  Upload
                </Button>
              </div>
            </div>
            <div className="row">
              <input
                type="text"
                name="pan_number"
                value={formData.owner.pan_number}
                onChange={(e) => handleOwnerDetailsInputChange(e, "uppercase")}
                placeholder="Enter PAN Number"
                className="input full"
                required
                pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                maxLength={10}
              />
              <div className="end row full">
                {uploads.pan_document_url ? (
                  <Button className="ghost_gray" onClick={handleViewPan}>
                    Preview
                  </Button>
                ) : (
                  <span className="preview small gray">No image uploaded</span>
                )}
                <Button className="upload" onClick={handlePanUpload}>
                  Upload
                </Button>
              </div>
            </div>
            <SubmitButton form="owner-details-form" end>
              SAVE
            </SubmitButton>
          </FormWrapper>
        )}
      </ModalWrapper>

      {showFileUploadModal &&
        createPortal(
          <ImageUploadModal
            isVisible={showFileUploadModal}
            handleClose={() => setShowFileUploadModal(false)}
            fileType={UploadFileTypes.IMAGES}
            {...uploadOptions}
          />,
          document.body
        )}

      {showViewMediaModal &&
        createPortal(
          <ViewMediaModal
            isVisible={showViewMediaModal}
            handleClose={() => setShowViewMediaModal(false)}
            {...viewMediaOptions}
          />,
          document.body
        )}
    </>
  );
}

export default CreateCompanyModal;
